import { useState, useEffect, useRef } from 'react';
import { MainLayout } from '../components/layout/MainLayout';
import { CabecalhoPagina } from '../components/layout/CabecalhoPagina';
import { BarraAcoes } from '../components/common/Formulario';
import { weightCategoryApi, categoryTableApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Scale, Plus, Loader2, Edit, Trash2, Upload, FolderPlus, Table2, List, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { EstadoVazio } from '../components/common/Estados';

function formatWeight(val) {
  if (val === null || val === undefined) return '';
  const n = Number(val);
  if (Number.isInteger(n)) return `${n}`;
  return n.toFixed(1).replace(/\.0$/, '');
}

function CellDisplay({ cell }) {
  if (!cell) return <span className="text-[#3F3F46]">-</span>;
  if (cell.max_weight === null || cell.max_weight === undefined) {
    return <span className="text-red-400">+{formatWeight(cell.min_weight)}</span>;
  }
  return <span>{formatWeight(cell.max_weight)}</span>;
}

function SexBadge({ sex }) {
  if (sex === 'M') return <span className="text-micro bg-blue-900/40 text-blue-300 px-1.5 py-0.5 rounded">M</span>;
  if (sex === 'F') return <span className="text-micro bg-pink-900/40 text-pink-300 px-1.5 py-0.5 rounded">F</span>;
  return <span className="text-micro bg-[#27272A] text-[#A1A1AA] px-1.5 py-0.5 rounded">MF</span>;
}

function AgeDisplay({ min_age, max_age }) {
  if (max_age) return <span className="text-micro text-[#71717A]">{min_age}-{max_age} anos</span>;
  return <span className="text-micro text-[#71717A]">+{min_age} anos</span>;
}

export default function WeightCategoriesPage() {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [matrixData, setMatrixData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tableName, setTableName] = useState('');
  const [editingTable, setEditingTable] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    table_id: '',
    class_name: '',
    category_name: '',
    sex: 'MF',
    min_age: 0,
    max_age: '',
    min_weight: 0,
    max_weight: '',
  });

  const fetchTables = async () => {
    try {
      const { data } = await categoryTableApi.list();
      setTables(data);
      if (data.length > 0 && !selectedTable) {
        setSelectedTable(data[0]._id);
      }
    } catch (error) {
      toast.error('Erro ao carregar tabelas');
    }
  };

  const fetchMatrix = async () => {
    if (!selectedTable) {
      setMatrixData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await weightCategoryApi.matrix(selectedTable);
      setMatrixData(data);
    } catch (error) {
      toast.error('Erro ao carregar categorias');
      setMatrixData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, []);

  useEffect(() => {
    if (selectedTable) fetchMatrix();
    else { setMatrixData(null); setLoading(false); }
  }, [selectedTable]);

  const handleTableSubmit = async (e) => {
    e.preventDefault();
    if (!tableName.trim()) return;
    setSaving(true);
    try {
      if (editingTable) {
        await categoryTableApi.update(editingTable._id, { name: tableName });
        toast.success('Tabela atualizada');
      } else {
        const { data } = await categoryTableApi.create({ name: tableName });
        toast.success('Tabela criada');
        setSelectedTable(data._id);
      }
      setIsTableDialogOpen(false);
      setTableName('');
      setEditingTable(null);
      fetchTables();
    } catch (error) {
      toast.error('Erro ao salvar tabela');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTable = async (id) => {
    if (!window.confirm('Excluir esta tabela e todas suas categorias?')) return;
    try {
      await categoryTableApi.delete(id);
      toast.success('Tabela excluida');
      if (selectedTable === id) {
        const remaining = tables.filter(t => t._id !== id);
        setSelectedTable(remaining.length > 0 ? remaining[0]._id : '');
      }
      fetchTables();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Erro ao excluir tabela';
      toast.error(msg);
    }
  };

  const handleSetDefaultTable = async (id) => {
    try {
      await categoryTableApi.setDefault(id);
      toast.success('Tabela definida como padrao do sistema');
      fetchTables();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Erro ao definir padrao';
      toast.error(msg);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!selectedTable) {
      toast.error('Selecione uma tabela antes de importar');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const { data } = await weightCategoryApi.upload(file, selectedTable);
      toast.success(data.message || 'Categorias importadas!');
      fetchMatrix();
    } catch (error) {
      toast.error('Erro ao importar arquivo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!formData.table_id) {
      toast.error('Selecione uma tabela');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        min_age: parseInt(formData.min_age) || 0,
        max_age: formData.max_age ? parseInt(formData.max_age) : null,
        min_weight: parseFloat(formData.min_weight) || 0,
        max_weight: formData.max_weight ? parseFloat(formData.max_weight) : null,
      };
      await weightCategoryApi.create(payload);
      toast.success('Categoria criada');
      setIsCategoryDialogOpen(false);
      fetchMatrix();
    } catch (error) {
      toast.error('Erro ao criar categoria');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Excluir esta categoria?')) return;
    try {
      await weightCategoryApi.delete(id);
      toast.success('Categoria excluida');
      fetchMatrix();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const selectedTableObj = tables.find(t => t._id === selectedTable);

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="weight-categories-page">
        {/* Header — título/contexto à esquerda, ações à direita */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <CabecalhoPagina
            className="min-w-0 flex-1"
            contexto="Configuração"
            titulo="Categorias de Peso"
            descricao="Gerencie tabelas de categorias por classe, idade e peso"
            icone={Scale}
          />
          <div className="flex gap-2 flex-wrap">
            <Dialog open={isTableDialogOpen} onOpenChange={(open) => { setIsTableDialogOpen(open); if (!open) { setTableName(''); setEditingTable(null); } }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="new-table-btn" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10">
                  <FolderPlus size={16} className="mr-2" /> Nova Tabela
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#141414] border-[#27272A] max-w-sm">
                <DialogHeader>
                  <DialogTitle className="text-white font-['Chivo']">{editingTable ? 'Editar Tabela' : 'Nova Tabela de Categorias'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleTableSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Nome da Tabela *</Label>
                    <Input value={tableName} onChange={(e) => setTableName(e.target.value)} placeholder="Ex: FJJPRIO, IBJJF, CBJJ" required data-testid="table-name-input" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                  </div>
                  <BarraAcoes contexto="dialogo">
                    <Button type="button" variant="outline" onClick={() => { setIsTableDialogOpen(false); setTableName(''); setEditingTable(null); }} className="border-[#27272A] text-[#A1A1AA]">Cancelar</Button>
                    <Button type="submit" disabled={saving} data-testid="save-table-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingTable ? 'Salvar' : 'Criar')}
                    </Button>
                  </BarraAcoes>
                </form>
              </DialogContent>
            </Dialog>

            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden" />
            <Button variant="outline" disabled={uploading || !selectedTable} onClick={() => fileInputRef.current?.click()} className="border-[#27272A] text-[#A1A1AA] hover:text-white">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload size={16} className="mr-2" />}
              Importar Excel
            </Button>

            <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button disabled={!selectedTable} data-testid="new-category-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold" onClick={() => setFormData({ table_id: selectedTable, class_name: '', category_name: '', sex: 'MF', min_age: 0, max_age: '', min_weight: 0, max_weight: '' })}>
                  <Plus size={16} className="mr-2" /> Nova Categoria
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#141414] border-[#27272A] max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-white font-['Chivo']">Nova Categoria</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCategorySubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Classe *</Label>
                      <Input value={formData.class_name} onChange={(e) => setFormData({ ...formData, class_name: e.target.value })} placeholder="Ex: ADULTO MASC" required data-testid="category-class" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Categoria *</Label>
                      <Input value={formData.category_name} onChange={(e) => setFormData({ ...formData, category_name: e.target.value })} placeholder="Ex: GALO, PLUMA, LEVE" required data-testid="category-name" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Sexo</Label>
                      <Select value={formData.sex} onValueChange={(v) => setFormData({ ...formData, sex: v })}>
                        <SelectTrigger data-testid="category-sex" className="bg-[#1A1A1A] border-[#27272A] text-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                          <SelectItem value="MF" className="text-white">Misto</SelectItem>
                          <SelectItem value="M" className="text-white">Masculino</SelectItem>
                          <SelectItem value="F" className="text-white">Feminino</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Idade Min</Label>
                      <Input type="number" value={formData.min_age} onChange={(e) => setFormData({ ...formData, min_age: e.target.value })} data-testid="category-min-age" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Idade Max</Label>
                      <Input type="number" value={formData.max_age} onChange={(e) => setFormData({ ...formData, max_age: e.target.value })} data-testid="category-max-age" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Peso Min (kg)</Label>
                      <Input type="number" step="0.1" value={formData.min_weight} onChange={(e) => setFormData({ ...formData, min_weight: e.target.value })} data-testid="category-min-weight" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Peso Max (kg)</Label>
                      <Input type="number" step="0.1" value={formData.max_weight} onChange={(e) => setFormData({ ...formData, max_weight: e.target.value })} data-testid="category-max-weight" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                  </div>
                  <BarraAcoes contexto="dialogo">
                    <Button type="button" variant="outline" onClick={() => setIsCategoryDialogOpen(false)} className="border-[#27272A] text-[#A1A1AA]">Cancelar</Button>
                    <Button type="submit" disabled={saving} data-testid="save-category" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar'}
                    </Button>
                  </BarraAcoes>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Table selector */}
        {tables.length > 0 && (
          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-sm">Tabelas de Categorias</Label>
            <div className="flex flex-wrap gap-2">
              {tables.map((t) => (
                <div key={t._id} className="flex items-center">
                  <Button
                    size="sm"
                    onClick={() => setSelectedTable(t._id)}
                    data-testid={`filter-table-${t._id}`}
                    className={`rounded-r-none ${selectedTable === t._id ? 'bg-[#D4AF37] text-black hover:bg-[#E5C158]' : 'bg-[#1A1A1A] border border-[#27272A] text-[#A1A1AA] hover:text-white'}`}
                  >
                    <Table2 size={16} className="mr-1.5" /> {t.name}
                    {t.is_default && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-micro font-bold bg-[#D4AF37]/25 text-[#D4AF37] border border-[#D4AF37]/50" data-testid={`badge-default-${t._id}`}>
                        ★ PADRÃO
                      </span>
                    )}
                  </Button>
                  <Button size="sm" onClick={() => handleSetDefaultTable(t._id)}
                    disabled={t.is_default}
                    title={t.is_default ? 'Ja e a tabela padrao' : 'Definir como padrao do sistema'}
                    data-testid={`btn-set-default-${t._id}`}
                    className="rounded-none bg-[#1A1A1A] border border-l-0 border-[#27272A] text-[#D4AF37] hover:text-[#E5C158] disabled:opacity-30 disabled:hover:text-[#D4AF37] px-2">
                    <Star size={12} fill={t.is_default ? 'currentColor' : 'none'} />
                  </Button>
                  <Button size="sm" onClick={() => { setEditingTable(t); setTableName(t.name); setIsTableDialogOpen(true); }}
                    className="rounded-none bg-[#1A1A1A] border border-l-0 border-[#27272A] text-[#A1A1AA] hover:text-white px-2">
                    <Edit size={12} />
                  </Button>
                  <Button size="sm" onClick={() => handleDeleteTable(t._id)}
                    className="rounded-l-none bg-[#1A1A1A] border border-l-0 border-[#27272A] text-red-400 hover:text-red-300 px-2">
                    <Trash2 size={12} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
          </div>
        ) : tables.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio icone={Scale} titulo="Nenhuma tabela cadastrada" descricao="Crie uma tabela e importe um arquivo Excel com as categorias" />
            </CardContent>
          </Card>
        ) : !matrixData || matrixData.total === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio
                icone={Scale}
                titulo={`Nenhuma categoria em "${selectedTableObj?.name}"`}
                descricao="Importe um arquivo Excel no formato matricial ou adicione manualmente"
                acao={
                  <Button onClick={() => fileInputRef.current?.click()} className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                    <Upload size={16} className="mr-2" /> Importar Excel
                  </Button>
                }
              />
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-white font-['Chivo'] flex items-center gap-2">
                  <Table2 size={20} className="text-[#D4AF37]" />
                  {selectedTableObj?.name}
                  <span className="text-sm text-[#71717A] font-normal ml-2">{matrixData.total} categorias</span>
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="tabela tabela--densa w-full text-sm" data-testid="category-matrix-table">
                  <thead>
                    <tr className="border-b border-[#27272A]">
                      <th className="sticky left-0 z-10 bg-[#141414] text-left py-3 px-3 text-[#D4AF37] font-bold min-w-[120px]">
                        Categoria
                      </th>
                      {matrixData.classes.map((cls) => (
                        <th key={cls.name} className="text-center py-2 px-2 min-w-[90px]">
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-white text-xs font-semibold leading-tight">{cls.name}</span>
                            <div className="flex items-center gap-1">
                              <SexBadge sex={cls.sex} />
                              <AgeDisplay min_age={cls.min_age} max_age={cls.max_age} />
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrixData.categories.map((catName, idx) => (
                      <tr key={catName} className={`border-b border-[#27272A]/40 ${idx % 2 === 0 ? 'bg-[#141414]' : 'bg-[#1A1A1A]/30'}`}>
                        <td className="sticky left-0 z-10 py-2 px-3 font-semibold text-[#D4AF37] text-xs bg-inherit">
                          {catName}
                        </td>
                        {matrixData.classes.map((cls) => {
                          const cell = matrixData.matrix[catName]?.[cls.name];
                          return (
                            <td key={cls.name} className="text-center py-2 px-2 text-[#E5E5E5] text-xs">
                              <CellDisplay cell={cell} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}
